/**
 * Error monitoring and logging utility
 * Helps track and diagnose frequent errors in the application
 */

class ErrorMonitor {
  constructor() {
    this.errorCounts = new Map();
    this.recentErrors = [];
    this.maxRecentErrors = 50;
  }

  /**
   * Logs an error and tracks its frequency
   * @param {Error} error - The error object
   * @param {string} context - Where the error occurred
   * @param {Object} metadata - Additional context
   */
  logError(error, context, metadata = {}) {
    const errorKey = `${context}:${error.message}`;
    const currentCount = this.errorCounts.get(errorKey) || 0;
    this.errorCounts.set(errorKey, currentCount + 1);

    const errorEntry = {
      timestamp: new Date().toISOString(),
      context,
      message: error.message,
      stack: error.stack,
      response: error.response?.data,
      status: error.response?.status,
      metadata,
      count: currentCount + 1
    };

    this.recentErrors.unshift(errorEntry);
    if (this.recentErrors.length > this.maxRecentErrors) {
      this.recentErrors = this.recentErrors.slice(0, this.maxRecentErrors);
    }

    // Log to console with context
    console.group(`🚨 Error in ${context} (occurred ${currentCount + 1} times)`);
    console.error('Message:', error.message);
    if (error.response) {
      console.error('Status:', error.response.status);
      console.error('Response:', error.response.data);
    }
    if (metadata && Object.keys(metadata).length > 0) {
      console.error('Metadata:', metadata);
    }
    console.error('Stack:', error.stack);
    console.groupEnd();

    // Alert if error is happening too frequently
    if (currentCount + 1 >= 5) {
      console.warn(`⚠️ Error "${error.message}" in ${context} has occurred ${currentCount + 1} times!`);
    }
  }

  /**
   * Gets error statistics
   * @returns {Object} - Error statistics
   */
  getStats() {
    const mostFrequent = Array.from(this.errorCounts.entries())
      .sort(([, a], [, b]) => b - a)
      .slice(0, 10);

    return {
      totalErrors: this.recentErrors.length,
      uniqueErrors: this.errorCounts.size,
      mostFrequent: mostFrequent.map(([key, count]) => ({ error: key, count })),
      recentErrors: this.recentErrors.slice(0, 10)
    };
  }

  /**
   * Gets recent errors for debugging
   * @param {number} limit - Number of recent errors to return
   * @returns {Array} - Recent errors
   */
  getRecentErrors(limit = 10) {
    return this.recentErrors.slice(0, limit);
  }

  /**
   * Clears error history
   */
  clear() {
    this.errorCounts.clear();
    this.recentErrors = [];
  }

  /**
   * Exports error data for support
   * @returns {string} - JSON string of error data
   */
  exportErrorData() {
    return JSON.stringify({
      timestamp: new Date().toISOString(),
      stats: this.getStats(),
      userAgent: navigator.userAgent,
      url: window.location.href
    }, null, 2);
  }
}

// Create global error monitor instance
export const errorMonitor = new ErrorMonitor();

// Enhanced error handler that also monitors errors
export const handleErrorWithMonitoring = (error, handlers = {}, context = '') => {
  // Log to error monitor
  errorMonitor.logError(error, context, {
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString()
  });

  // Use existing error handler
  const { handleError } = require('./errorHandler');
  return handleError(error, handlers, context);
};

// Development helpers (only available in development)
if (process.env.NODE_ENV === 'development') {
  // Make error monitor available globally for debugging
  window.errorMonitor = errorMonitor;
  
  // Add console command to get error stats
  window.getErrorStats = () => {
    console.table(errorMonitor.getStats().mostFrequent);
    return errorMonitor.getStats();
  };
  
  // Add console command to export error data
  window.exportErrors = () => {
    const data = errorMonitor.exportErrorData();
    console.log('Error data:', data);
    
    // Try to copy to clipboard
    if (navigator.clipboard) {
      navigator.clipboard.writeText(data).then(() => {
        console.log('Error data copied to clipboard!');
      });
    }
    
    return data;
  };
}

export default errorMonitor;
